import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useCreateKitAssetsMutation, useCreateReelMutation } from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { ReelDurationSec } from '../types';
import { useVideoCreditGate } from '../credits/useVideoCreditGate';
import { kitRefusal } from '../releases/assets/kitErrors';
import { studioMessages } from '../studio/studioMessages';
import { StudioActionFeedback, studioActionErrorFeedback } from '../studio/studioActionErrorText';
import { RevisionEntry } from './revisionModel';
import { RevisionChoice, revisionCost, revisionKitItem } from './revisionMakeItem';

type MakeOutcome = { ok: true; created: number } | { ok: false; error: unknown };

/**
 * "Make rN" of the dialog (design d3). A kit asset posts one item with the params of the revision
 * and the choices (`batch: false`); a reel starts a reel of the same song, length, waveform choice and
 * voice, which the server numbers as the next revision. A balance below the cost opens
 * the credit packs and sends nothing. A failure shows next to the button (`feedback`).
 */
export function useMakeRevision(input: { accountId: number; catalogItemUuid: string; onMade: () => void }) {
  const { accountId, catalogItemUuid, onMade } = input;
  const intl = useIntl();
  const [createKitAssets] = useCreateKitAssetsMutation();
  const [createReel] = useCreateReelMutation();
  const { canSpend } = useVideoCreditGate(accountId);
  const [isMaking, setIsMaking] = useState(false);
  const [feedback, setFeedback] = useState<StudioActionFeedback | null>(null);

  const makeKit = useCallback(
    async (entry: RevisionEntry, choice: RevisionChoice): Promise<MakeOutcome> => {
      const item = revisionKitItem(entry, choice);
      if (item === null) {
        return { ok: true, created: 0 };
      }
      const result = await createKitAssets({ accountId, catalogItemUuid, items: [item], batch: false });
      return isSuccessfulResult(result)
        ? { ok: true, created: result.data.created.length }
        : { ok: false, error: result.error };
    },
    [accountId, catalogItemUuid, createKitAssets]
  );

  const makeReel = useCallback(
    async (entry: RevisionEntry, choice: RevisionChoice): Promise<MakeOutcome> => {
      const { reel } = entry;
      if (reel === null) {
        return { ok: true, created: 0 };
      }
      const result = await createReel({
        accountId,
        catalogItemUuid,
        voiceId: reel.voiceId,
        durationSec: reel.durationSec as ReelDurationSec,
        waveform: choice.waveform ?? reel.waveform,
        instructions: reel.instructions ?? undefined,
      });
      return isSuccessfulResult(result) ? { ok: true, created: 1 } : { ok: false, error: result.error };
    },
    [accountId, catalogItemUuid, createReel]
  );

  const make = useCallback(
    async (entry: RevisionEntry, choice: RevisionChoice): Promise<void> => {
      if (!canSpend(revisionCost(entry, choice))) {
        return;
      }
      setFeedback(null);
      setIsMaking(true);
      const outcome = await (entry.reel === null ? makeKit(entry, choice) : makeReel(entry, choice));
      setIsMaking(false);
      if (outcome.ok) {
        if (outcome.created === 0) {
          setFeedback({ tone: 'info', message: intl.formatMessage(studioMessages.busy) });
          return;
        }
        onMade();
        return;
      }
      const result = outcome;
      const refusal = kitRefusal(result.error);
      if (refusal === 'videoCredits') {
        return;
      }
      if (refusal === 'busy') {
        setFeedback({ tone: 'info', message: intl.formatMessage(studioMessages.busy) });
        return;
      }
      setFeedback(studioActionErrorFeedback(intl, 'make a new revision', result.error));
    },
    [canSpend, intl, makeKit, makeReel, onMade]
  );

  const clearFeedback = useCallback(() => setFeedback(null), []);

  return { make, isMaking, feedback, clearFeedback };
}
